import { isToolUIPart, type UIMessage } from 'ai'
import {
  SessionKey,
  sessionStorageService,
} from '@/services/SessionStorage/SessionStorageService'
import { decodeApprovalCard, encodeApprovalCard } from './contract'

/**
 * Ask AttorneyShare keeps the conversation in the browser only: in memory,
 * plus a copy in the app's session storage (SessionStorageService) tagged
 * with the signed-in user, so a reload picks up where they left off. The
 * session store is wiped on sign-out, and a copy tagged with another user is
 * ignored. Nothing is stored server-side.
 */

/** Keep the stored copy small: the newest messages only. */
export const ASSISTANT_HISTORY_MAX_MESSAGES = 40

const base64UrlDecode = (value: string) =>
  atob(value.replace(/-/g, '+').replace(/_/g, '/'))

/**
 * The signed-in user's id, read from the access token's `id` claim. Only
 * used to scope the stored history; nothing is verified or sent anywhere.
 */
export const readAssistantUserId = (token: string | null): string | null => {
  if (!token) return null
  try {
    const payload = JSON.parse(base64UrlDecode(token.split('.')[1] ?? ''))
    return typeof payload?.id === 'string' && payload.id ? payload.id : null
  } catch {
    return null
  }
}

/** Tool states that are finished and safe to replay to the model. */
const SETTLED_TOOL_STATES = new Set([
  'output-available',
  'output-error',
  'output-denied',
])

const CLIENT_CONTACT_KEYS = ['firstName', 'lastName', 'phone', 'email']

/**
 * Blanks client names and contact details anywhere they appear (referral
 * drafts, post_referral inputs) so they never sit in session storage.
 */
const redactClientContact = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(redactClientContact)
  if (!value || typeof value !== 'object') return value
  return Object.fromEntries(
    Object.entries(value).map(([key, inner]) => {
      if (key !== 'client' || !inner || typeof inner !== 'object') {
        return [key, redactClientContact(inner)]
      }
      const client = { ...(inner as Record<string, unknown>) }
      CLIENT_CONTACT_KEYS.filter(field => field in client).forEach(field => {
        client[field] = null
      })
      return [key, client]
    })
  )
}

/**
 * Pasted notes and transcripts are full of client details; the stored copy
 * keeps only the draft built from them (itself redacted).
 */
const PASTED_TEXT_TOOL_TYPE = 'tool-draft_referral_from_text'
const PASTED_TEXT_PLACEHOLDER = '(pasted notes are not kept after a reload)'

/** The approval card row that names the client (post_referral). */
const CLIENT_CARD_ROW_LABEL = 'Client'

/**
 * The Approve/Cancel card travels as a JSON string in
 * `approval.requestReason`, so the object walk above never sees it. Decode
 * it, blank the referral preview's client and drop the "Client" row, then
 * re-encode. A reason that is not a card is dropped rather than kept as is.
 */
const redactApprovalReason = (reason: string): string | undefined => {
  const card = decodeApprovalCard(reason)
  if (!card) return undefined
  const redacted = redactClientContact(card) as typeof card
  return encodeApprovalCard({
    ...redacted,
    rows: redacted.rows.filter(row => row.label !== CLIENT_CARD_ROW_LABEL),
  })
}

const redactToolPart = <P extends object>(part: P): P => {
  const redacted = redactClientContact(part) as P & {
    type?: string
    approval?: { requestReason?: string }
  }
  const reason = redacted.approval?.requestReason
  const withCard =
    typeof reason === 'string'
      ? {
          ...redacted,
          approval: {
            ...redacted.approval,
            requestReason: redactApprovalReason(reason),
          },
        }
      : redacted
  if (withCard.type !== PASTED_TEXT_TOOL_TYPE) return withCard
  return { ...withCard, input: { text: PASTED_TEXT_PLACEHOLDER } }
}

/**
 * The copy that goes to storage: unfinished actions (including changes still
 * waiting for Approve/Cancel) are dropped, since their approvals expire and
 * they cannot resume after a reload, and client contact and pasted notes are
 * blanked in action inputs, outputs and approval cards.
 *
 * Deliberate gap: what the user typed into the composer is kept as written,
 * pasted transcripts included, because dropping it would break the
 * conversation the model replays. That copy lives only in this tab's session
 * storage, tagged with the user and wiped on sign-out.
 */
export const toStoredMessages = <M extends UIMessage>(messages: M[]): M[] =>
  messages
    .slice(-ASSISTANT_HISTORY_MAX_MESSAGES)
    .map(message => ({
      ...message,
      parts: message.parts
        .filter(
          part => !isToolUIPart(part) || SETTLED_TOOL_STATES.has(part.state)
        )
        .map(part => (isToolUIPart(part) ? redactToolPart(part) : part)),
    }))
    .filter(message => message.parts.length > 0) as M[]

interface StoredHistory {
  userId: string
  messages: UIMessage[]
}

/**
 * The stored conversation for this user, or nothing when the copy belongs to
 * someone else (the session wipes it on sign-out anyway).
 */
export const loadAssistantHistory = <M extends UIMessage>(
  userId: string | null
): M[] => {
  if (!userId) return []
  try {
    const raw = sessionStorageService.getItem(SessionKey.ASSISTANT_HISTORY)
    const parsed = raw ? (JSON.parse(raw) as Partial<StoredHistory>) : null
    if (parsed?.userId !== userId || !Array.isArray(parsed.messages)) return []
    return parsed.messages.filter(
      m => m && typeof m.id === 'string' && Array.isArray(m.parts)
    ) as M[]
  } catch {
    return []
  }
}

export const saveAssistantHistory = (
  userId: string | null,
  messages: UIMessage[]
) => {
  if (!userId) return
  try {
    const stored = toStoredMessages(messages)
    if (stored.length === 0) {
      sessionStorageService.removeItem(SessionKey.ASSISTANT_HISTORY)
      return
    }
    const value: StoredHistory = { userId, messages: stored }
    sessionStorageService.setItem(
      SessionKey.ASSISTANT_HISTORY,
      JSON.stringify(value)
    )
  } catch {
    // Full or blocked storage: the conversation still lives in memory.
  }
}
